'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';

interface HandRaiseContextType {
  handRaisedParticipants: Set<string>;
  setHandRaised: (participantId: string, raised: boolean) => void;
  getHandRaisedCount: () => number;
}

const HandRaiseContext = createContext<HandRaiseContextType | undefined>(undefined);

export function HandRaiseProvider({ children }: { children: React.ReactNode }) {
  const [handRaisedParticipants, setHandRaisedParticipants] = useState<Set<string>>(new Set());

  const setHandRaised = useCallback((participantId: string, raised: boolean) => {
    setHandRaisedParticipants((prev) => {
      const newSet = new Set(prev);
      if (raised) {
        newSet.add(participantId);
      } else {
        newSet.delete(participantId);
      }
      return newSet;
    });
  }, []);

  const getHandRaisedCount = useCallback(() => {
    return handRaisedParticipants.size;
  }, [handRaisedParticipants]);

  const value: HandRaiseContextType = {
    handRaisedParticipants,
    setHandRaised,
    getHandRaisedCount,
  };

  return <HandRaiseContext.Provider value={value}>{children}</HandRaiseContext.Provider>;
}

export function useHandRaise() {
  const context = useContext(HandRaiseContext);
  if (context === undefined) {
    throw new Error('useHandRaise must be used within a HandRaiseProvider');
  }
  return context;
}
